import React from "react";

interface RichTextDisplayProps {
  content: string;
  className?: string;
}

/**
 * Renders plain text with rich formatting:
 * - Line breaks preserved
 * - Bullet points (-, *, •) rendered as list items
 * - Numbered lists (1., 2., etc.) rendered properly
 * - Quoted text ("..." or '...') highlighted - handles apostrophes inside
 * - Bold text (**text** or __text__) rendered bold
 */
const RichTextDisplay: React.FC<RichTextDisplayProps> = ({ content, className = "" }) => {
  if (!content) {
    return null;
  }

  const renderLine = (line: string, index: number) => {
    // Check if it's a bullet point line
    const bulletMatch = line.match(/^[\s]*[-*•]\s+(.*)$/);
    if (bulletMatch) {
      return (
        <li key={index} className="ml-4 list-disc list-inside text-gray-700 leading-relaxed">
          {renderInlineFormatting(bulletMatch[1])}
        </li>
      );
    }

    // Check if it's a numbered list item
    const numberedMatch = line.match(/^[\s]*(\d+)[.)]\s+(.*)$/);
    if (numberedMatch) {
      return (
        <li key={index} className="ml-4 list-decimal list-inside text-gray-700 leading-relaxed">
          {renderInlineFormatting(numberedMatch[2])}
        </li>
      );
    }

    // Check if it's a section header (all caps or ends with colon)
    const headerMatch = line.match(/^([A-Z][A-Z\s]+):?\s*$/) || line.match(/^(.+):$/);
    if (headerMatch && line.length < 50) {
      return (
        <p key={index} className="font-semibold text-gray-900 mt-3 mb-1">
          {headerMatch[1]}
        </p>
      );
    }

    // Empty line = paragraph break
    if (line.trim() === "") {
      return <div key={index} className="h-2" />;
    }

    // Regular paragraph
    return (
      <p key={index} className="text-gray-700 leading-relaxed">
        {renderInlineFormatting(line)}
      </p>
    );
  };

  const renderInlineFormatting = (text: string): React.ReactNode => {
    if (!text) return null;

    const parts: React.ReactNode[] = [];
    let keyIndex = 0;

    const segments: { start: number; end: number; content: string }[] = [];

    // First pass: handle double quotes - simple and reliable
    const doubleQuoteRegex = /"([^"]+)"/g;
    let match;

    while ((match = doubleQuoteRegex.exec(text)) !== null) {
      segments.push({
        start: match.index,
        end: match.index + match[0].length,
        content: match[1]
      });
    }

    // Second pass: handle single quotes more carefully
    // We need to find 'quoted text' but NOT apostrophes like "I've" or "don't"
    // Strategy: Look for patterns like: word: 'text' or starts with 'text'
    // A quote typically: starts after punctuation/space, contains multiple words, ends before punctuation/space

    // Find potential single-quoted sections
    let i = 0;
    while (i < text.length) {
      if (text[i] === "'") {
        // Check if this looks like the START of a quote (not an apostrophe)
        // It's likely a quote start if:
        // 1. It's at the beginning of text, OR
        // 2. Previous char is space, colon, or opening bracket
        const prevChar = i > 0 ? text[i - 1] : ' ';
        const isQuoteStart = i === 0 || /[\s:(\[]/.test(prevChar);

        if (isQuoteStart) {
          // Look for the closing quote
          let j = i + 1;
          let foundClose = false;

          while (j < text.length) {
            if (text[j] === "'") {
              // Check if this is a closing quote (not an apostrophe)
              // It's a closing quote if next char is space, punctuation, or end
              const nextChar = j + 1 < text.length ? text[j + 1] : ' ';
              const isQuoteEnd = /[\s.,;:!?)\]]/.test(nextChar) || j + 1 >= text.length;

              if (isQuoteEnd) {
                const content = text.slice(i + 1, j);
                // Only treat as quote if it has some substance (not empty, has letters)
                if (content.length > 0 && /[a-zA-Z]/.test(content)) {
                  // Check for overlap with existing segments
                  const overlaps = segments.some(
                    seg => (i >= seg.start && i < seg.end) || (j >= seg.start && j < seg.end)
                  );
                  if (!overlaps) {
                    segments.push({
                      start: i,
                      end: j + 1,
                      content: content
                    });
                  }
                }
                foundClose = true;
                i = j;
                break;
              }
            }
            j++;
          }

          if (!foundClose) {
            i++;
          }
        } else {
          i++;
        }
      } else {
        i++;
      }
    }

    // Sort segments by start position
    segments.sort((a, b) => a.start - b.start);

    // Build the result
    let lastIndex = 0;
    segments.forEach((segment) => {
      // Add text before this segment
      if (segment.start > lastIndex) {
        const beforeText = text.slice(lastIndex, segment.start);
        parts.push(
          <span key={keyIndex++}>
            {formatBoldAndItalic(beforeText)}
          </span>
        );
      }

      // Add the quoted text with styling
      parts.push(
        <span
          key={keyIndex++}
          className="bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded-md italic mx-0.5 inline"
        >
          "{segment.content}"
        </span>
      );

      lastIndex = segment.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key={keyIndex++}>
          {formatBoldAndItalic(text.slice(lastIndex))}
        </span>
      );
    }

    return parts.length > 0 ? parts : formatBoldAndItalic(text);
  };

  const formatBoldAndItalic = (text: string): React.ReactNode => {
    if (!text) return null;

    // Handle **bold** or __bold__
    const boldRegex = /(\*\*|__)([^*_]+)\1/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={keyIndex++}>{text.slice(lastIndex, match.index)}</span>);
      }
      parts.push(
        <strong key={keyIndex++} className="font-semibold">
          {match[2]}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(<span key={keyIndex++}>{text.slice(lastIndex)}</span>);
    }

    return parts.length > 0 ? parts : text;
  };

  const lines = content.split("\n");

  // Group consecutive bullet/numbered items into lists
  const elements: React.ReactNode[] = [];
  let currentList: React.ReactNode[] = [];
  let listType: "ul" | "ol" | null = null;

  lines.forEach((line, index) => {
    const isBullet = /^[\s]*[-*•]\s+/.test(line);
    const isNumbered = /^[\s]*\d+[.)]\s+/.test(line);

    if (isBullet) {
      if (listType !== "ul" && currentList.length > 0) {
        elements.push(
          listType === "ol" ? (
            <ol key={`list-${index}`} className="space-y-1 my-2">
              {currentList}
            </ol>
          ) : (
            <ul key={`list-${index}`} className="space-y-1 my-2">
              {currentList}
            </ul>
          )
        );
        currentList = [];
      }
      listType = "ul";
      currentList.push(renderLine(line, index));
    } else if (isNumbered) {
      if (listType !== "ol" && currentList.length > 0) {
        elements.push(
          listType === "ul" ? (
            <ul key={`list-${index}`} className="space-y-1 my-2">
              {currentList}
            </ul>
          ) : (
            <ol key={`list-${index}`} className="space-y-1 my-2">
              {currentList}
            </ol>
          )
        );
        currentList = [];
      }
      listType = "ol";
      currentList.push(renderLine(line, index));
    } else {
      // Flush current list if exists
      if (currentList.length > 0) {
        elements.push(
          listType === "ol" ? (
            <ol key={`list-${index}`} className="space-y-1 my-2">
              {currentList}
            </ol>
          ) : (
            <ul key={`list-${index}`} className="space-y-1 my-2">
              {currentList}
            </ul>
          )
        );
        currentList = [];
        listType = null;
      }
      elements.push(renderLine(line, index));
    }
  });

  // Flush remaining list
  if (currentList.length > 0) {
    elements.push(
      listType === "ol" ? (
        <ol key="list-final" className="space-y-1 my-2">
          {currentList}
        </ol>
      ) : (
        <ul key="list-final" className="space-y-1 my-2">
          {currentList}
        </ul>
      )
    );
  }

  return <div className={`space-y-1 ${className}`}>{elements}</div>;
};

export default RichTextDisplay;
